import {View, Text, TouchableOpacity, ViewStyle} from 'react-native';
import React, {PropsWithChildren} from 'react';

import Image from '../custom/Image';
import {theme} from '../../constants';
import {ProductType} from '../../types';
import {useAppNavigation, useAppDispatch} from '../../hooks';
import ProductNew from '../product/ProductNew';
import ProductName from '../product/ProductName';
import ProductPrice from '../product/ProductPrice';
import {hasPositiveWeight, formatCaloriesLabel} from '../../utils/product';
import {svg} from '../../assets/svg';
import {text} from '../../text';
import {quantityInCart, addedToCartMessage} from '../../utils';
import {
  addToCart,
  removeFromCart,
  fullRemoveFromCart,
} from '../../store/slices/cartSlice';

type Props = PropsWithChildren<{
  item: ProductType;
  lastItem?: boolean;
}>;

const MenuListItem: React.FC<Props> = ({item, lastItem}): JSX.Element => {
  const navigation = useAppNavigation();
  const dispatch = useAppDispatch();
  const hasWeight = hasPositiveWeight(item.weight);
  const caloriesLabel = formatCaloriesLabel(item.calories, {appendUnit: false});
  const hasCalories = typeof caloriesLabel === 'string' && caloriesLabel.length > 0;
  const hasAddons = Array.isArray(item.addons) && item.addons.length > 0;
  const quantity = quantityInCart(item) ?? 0;

  const handleNavigate = () => {
    navigation.navigate('Product', {item});
  };

  const handleAdd = () => {
    if (hasAddons) {
      handleNavigate();
      return;
    }

    dispatch(addToCart(item));

    if (quantity === 0) {
      addedToCartMessage(item);
    }
  };

  const handleDecrease = () => {
    if (quantity <= 1) {
      dispatch(fullRemoveFromCart(item));
      return;
    }

    dispatch(removeFromCart(item));
  };

  const renderActions = () => {
    if (hasAddons) {
      return (
        <TouchableOpacity
          onPress={handleNavigate}
          style={{
            backgroundColor: theme.colors.accentPrimary,
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 10,
          }}
        >
          <text.T14
            style={{
              color: theme.colors.backgroundPrimary,
              textTransform: 'capitalize',
            }}
          >
            Choisir
          </text.T14>
        </TouchableOpacity>
      );
    }

    if (quantity === 0) {
      return (
        <TouchableOpacity
          onPress={handleAdd}
          style={{
            backgroundColor: theme.colors.accentPrimary,
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 10,
          }}
        >
          <text.T14
            style={{
              color: theme.colors.backgroundPrimary,
              textTransform: 'capitalize',
            }}
          >
            Ajouter
          </text.T14>
        </TouchableOpacity>
      );
    }

    const actionButtonStyle: ViewStyle = {
      padding: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#D5DCE3',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return (
      <View style={{flexDirection: 'row', alignItems: 'center'}}>
        <TouchableOpacity onPress={handleDecrease} style={actionButtonStyle}>
          <svg.DishMinusSvg color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text
          style={{
            ...theme.fonts.DMSans_700Bold,
            fontSize: 14,
            marginHorizontal: 12,
            color: theme.colors.textPrimary,
          }}
        >
          {quantity}
        </Text>
        <TouchableOpacity onPress={handleAdd} style={actionButtonStyle}>
          <svg.DishPlusSvg color={theme.colors.textPrimary} />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View
      style={{
        backgroundColor: theme.colors.backgroundPrimary,
        borderRadius: 10,
        marginBottom: lastItem ? 0 : 14,
        flexDirection: 'row',
        alignItems: 'center',
        paddingRight: 12,
      }}
    >
      <TouchableOpacity
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          flex: 1,
        }}
        activeOpacity={0.8}
        onPress={handleNavigate}
      >
        <View
          style={{
            width: 117,
            height: 117,
            margin: 8,
            borderRadius: 10,
            overflow: 'hidden',
            backgroundColor: '#E9F3F6',
          }}
        >
          {item.image ? (
            <Image
              source={{uri: item.image}}
              style={{width: '100%', height: '100%'}}
              resizeMode='cover'
            />
          ) : (
            <View
              style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ProductName item={item} style={{marginBottom: 0}} />
            </View>
          )}
          <ProductNew
            item={item}
            containerStyle={{
              position: 'absolute',
              top: 8,
              left: 8,
              padding: 7,
            }}
          />
        </View>
        <View style={{flex: 1, marginRight: 12, marginLeft: 4}}>
          <ProductName item={item} style={{marginBottom: 4}} />
          <Text
            numberOfLines={2}
            style={{
              ...theme.fonts.DMSans_400Regular,
              fontSize: 10,
              color: theme.colors.textMuted,
              marginBottom: 4,
              lineHeight: 10 * 1.5,
            }}
          >
            {item.description}
          </Text>
          {(hasWeight || hasCalories) && (
            <Text
              style={{
                ...theme.fonts.DMSans_400Regular,
                fontSize: 10,
                color: theme.colors.textMuted,
                marginBottom: 6,
                lineHeight: 10 * 1.5,
              }}
            >
              {hasWeight ? `${item.weight}` : ''}
              {hasWeight && hasCalories ? ' - ' : ''}
              {hasCalories ? caloriesLabel : ''}
            </Text>
          )}
          <ProductPrice item={item} />
        </View>
      </TouchableOpacity>
      <View
        style={{
          paddingVertical: 12,
          paddingLeft: 4,
        }}
      >
        {renderActions()}
      </View>
    </View>
  );
};

export default MenuListItem;
